<template>
	<ul>
		<li v-for="doc in sortedDocuments" :key="doc.id" class="border-b">
			<div
				class="py-2 flex flex-wrap items-center gap-2"
				:class="
					isHdnNativeDocumentTooLarge(doc.byteSize, doc.filename)
						? 'cursor-not-allowed bg-gray-50 text-gray-400'
						: 'hover:bg-gray-50 cursor-pointer'
				"
				@click="
					!isHdnNativeDocumentTooLarge(doc.byteSize, doc.filename) &&
					emit('select', doc)
				"
			>
				<button
					type="button"
					class="w-full min-w-0 text-left [overflow-wrap:anywhere]"
					:aria-label="`Koppel ${doc.filename}`"
					:disabled="isHdnNativeDocumentTooLarge(doc.byteSize, doc.filename)"
					@click.stop="emit('select', doc)"
				>
					{{ doc.filename }}
				</button>
				<button
					type="button"
					class="shrink-0 rounded border px-2 py-1 text-sm text-gray-700 hover:bg-gray-100"
					:aria-label="`Voorbeeld van ${doc.filename}`"
					@click.stop="emit('preview', doc)"
				>
					Voorbeeld
				</button>
				<span
					v-if="isHdnNativeDocumentTooLarge(doc.byteSize, doc.filename)"
					class="shrink-0 text-xs text-gray-400"
				>
					Te groot: {{ formatMegabytes(doc.byteSize) }} MB, max.
					{{ maxMegabytes }} MB
				</span>
				<template v-else>
					<span
						v-if="documentScores.get(doc.id)"
						class="shrink-0 px-2 py-0.5 rounded text-xs best-match"
					>
						Beste match
					</span>
					<span
						v-if="classificationMap.get(doc.id)"
						class="shrink-0 px-2 py-0.5 rounded text-xs"
						:class="[
							!documentScores.get(doc.id) && 'ml-auto',
							classificationMap.get(doc.id)!.confidence >=
							CLASSIFICATIE_HOGE_CONFIDENCE
								? 'classification-high'
								: classificationMap.get(doc.id)!.confidence >=
									  CLASSIFICATIE_MIN_CONFIDENCE
									? 'classification-medium'
									: 'classification-unknown',
						]"
						:title="`${Math.round(classificationMap.get(doc.id)!.confidence * 100)}% zekerheid`"
					>
						{{
							classificationMap.get(doc.id)!.confidence >=
							CLASSIFICATIE_MIN_CONFIDENCE
								? classificationMap.get(doc.id)!.label
								: CLASSIFICATIE_ONBEKEND_LABEL
						}}
					</span>
					<button
						v-if="extractionEnabled && classificationMap.get(doc.id)"
						class="shrink-0 text-gray-400 hover:text-gray-600 text-sm"
						:title="
							expandedDoc === doc.id
								? 'Verberg details'
								: 'Toon geëxtraheerde gegevens'
						"
						@click.stop="toggleExpand(doc.id)"
					>
						<AppIcon
							:name="expandedDoc === doc.id ? 'expand_less' : 'expand_more'"
							class="text-lg"
						/>
					</button>
				</template>
			</div>
			<DocumentExtractionDetails
				v-if="extractionEnabled && expandedDoc === doc.id"
				:document-id="doc.id"
				:classification-label="classificationMap.get(doc.id)?.label"
				compact
				class="pb-2 px-1"
			/>
		</li>
	</ul>
</template>

<script lang="ts" setup>
import type {
	DossierDocument,
	HdnRequestedDocument,
} from "@/composables/useLoanApplicationSortedDocuments";
import { computed, inject, ref, type Ref } from "vue";
import {
	CLASSIFICATIE_MIN_CONFIDENCE,
	CLASSIFICATIE_HOGE_CONFIDENCE,
	CLASSIFICATIE_ONBEKEND_LABEL,
	CLASSIFICATIE_NAAR_HDN,
	BESTANDSNAAM_KEYWORDS,
} from "@/parameters/documentClassificatie";
import {
	HDN_MAX_DOCUMENT_BYTES,
	isHdnNativeDocumentTooLarge,
} from "@/helpers/hdnDocumentSize";
import AppIcon from "@/components/AppIcon.vue";
import DocumentExtractionDetails from "./DocumentExtractionDetails.vue";
import { useFeatureFlag } from "@/composables/useFeatureFlag";

const extractionEnabled = useFeatureFlag("document-extraction-intermediary");

const props = defineProps<{
	availableDocuments: DossierDocument[];
	requestedDocumentType?: string;
	partijNawData?: HdnRequestedDocument["partijNawData"];
}>();
const emit = defineEmits<{
	select: [doc: DossierDocument];
	preview: [doc: DossierDocument];
}>();

const expandedDoc = ref<string | null>(null);

function toggleExpand(docId: string) {
	expandedDoc.value = expandedDoc.value === docId ? null : docId;
}

const classificationMap =
	inject<Ref<Map<string, { label: string; confidence: number }>>>(
		"classificationsMap",
	) ?? computed(() => new Map<string, { label: string; confidence: number }>());

const collator = new Intl.Collator("nl-NL", {
	sensitivity: "base",
});

const TUSSENVOEGSELS = new Set([
	"de",
	"van",
	"der",
	"het",
	"den",
	"ter",
	"ten",
	"te",
	"in",
	"op",
	"el",
	"al",
]);

function getNameFragments(
	nawData: HdnRequestedDocument["partijNawData"],
): string[] {
	if (!nawData) return [];

	const fragments: string[] = [];

	const firstname =
		nawData.correspondenceFirstname ?? nawData.givenNames ?? null;
	if (firstname) {
		for (const part of firstname.toLowerCase().split(/\s+/)) {
			if (part.length >= 3 && !TUSSENVOEGSELS.has(part)) fragments.push(part);
		}
	}

	const surname = nawData.correspondenceSurname ?? nawData.birthSurname ?? null;
	if (surname) {
		for (const part of surname.toLowerCase().split(/\s+/)) {
			if (part.length >= 3 && !TUSSENVOEGSELS.has(part)) fragments.push(part);
		}
	}

	return fragments;
}

function scoreDocument(doc: DossierDocument, requestedType: string): number {
	let score = 0;

	const classification = classificationMap.value.get(doc.id);
	if (
		classification &&
		classification.confidence >= CLASSIFICATIE_MIN_CONFIDENCE
	) {
		const hdnPrefixes = CLASSIFICATIE_NAAR_HDN[classification.label] ?? [];
		if (hdnPrefixes.some((prefix) => requestedType.startsWith(prefix))) {
			score += 100 * classification.confidence;
		}
	}

	if (score === 0) {
		const filename = doc.filename.toLowerCase();
		for (const [hdnPrefix, keywords] of Object.entries(BESTANDSNAAM_KEYWORDS)) {
			if (requestedType.startsWith(hdnPrefix)) {
				if (keywords.some((kw) => filename.includes(kw))) {
					score += 50;
				}
				break;
			}
		}
	}

	// Signaal 3: persoonsmatching — alleen als tie-breaker bij bestaande type-match
	if (score > 0) {
		const nameFragments = getNameFragments(props.partijNawData);
		if (nameFragments.length > 0) {
			const filename = doc.filename.toLowerCase();
			if (nameFragments.some((fragment) => filename.includes(fragment))) {
				score += 30;
			}
		}
	}

	return score;
}

const documentScores = computed(() => {
	const map = new Map<string, number>();
	if (!props.requestedDocumentType) return map;

	let bestId: string | null = null;
	let bestScore = 0;

	for (const doc of props.availableDocuments) {
		const score = scoreDocument(doc, props.requestedDocumentType);
		if (score > bestScore) {
			bestScore = score;
			bestId = doc.id;
		}
	}

	if (bestId && bestScore > 0) {
		map.set(bestId, bestScore);
	}

	return map;
});

const sortedDocuments = computed(() => {
	if (!props.requestedDocumentType) {
		return props.availableDocuments.toSorted((a, b) =>
			collator.compare(a.filename, b.filename),
		);
	}

	const requestedType = props.requestedDocumentType;

	return props.availableDocuments.toSorted((a, b) => {
		const scoreA = scoreDocument(a, requestedType);
		const scoreB = scoreDocument(b, requestedType);

		if (scoreA !== scoreB) return scoreB - scoreA;

		return collator.compare(a.filename, b.filename);
	});
});

function formatMegabytes(byteSize: number) {
	return (byteSize / 1024 / 1024).toFixed(1);
}

const maxMegabytes = formatMegabytes(HDN_MAX_DOCUMENT_BYTES);
</script>

<style scoped>
.classification-high {
	background: color-mix(in srgb, var(--color-success) 15%, white);
	color: var(--color-success);
}

.classification-medium {
	background: var(--background-secondary);
	color: var(--text-secondary);
}

.classification-unknown {
	background: color-mix(in srgb, var(--color-warn) 15%, white);
	color: var(--color-warn);
	font-style: italic;
}

.best-match {
	background: color-mix(in srgb, var(--accent-primary) 15%, white);
	color: var(--accent-primary);
	font-weight: 500;
}
</style>
